import { HStack } from "IZOX/_X";
import { useCallback, useEffect, useState } from "react";
import QFButton from "../QForm/QFButton";
import { ColorX } from "IZOX/STATIC";
import _ from "lodash";

/**
 * @typedef {Object} option
 * @property {String} val - The value of the option
 * @property {String} cap - The display label of the option
 * @property {String} color - The color associated with the option
 * @property {String} [bgColor] - Optional background color for the option
 * @property {String} [icon] - Optional icon for the option
 * 
 * @typedef {Object} typselectorparams
 * @property {(selected) => void} onChange - Callback function when selection changes
 * @property {String | String[]} defaultValue - Default selected value(s)
 * @property {Boolean} multiple - Whether multiple selections are allowed
 * @property {Object[]} options - Array of option objects {value: String, label: String}
 * @property {import("IZOX/_X/HStack").hstackparams} [hstackparams] - Optional parameters for horizontal stack layout
 * @property {String | String[]} [value] - Controlled selected value(s)
 * @property {Boolean} [readOnly] - Whether the selector is read-only
 * @property {import("../QForm/QFButton").qfbuttonparams} [qfbuttonparams] - Optional parameters for QFButton
 * 
 * @param {typselectorparams} param0 
 */
function TypeSelector({
  onChange,
  defaultValue,
  multiple = false,
  options = [],
  valAccessor = "val",
  capAccessor = "cap",
  colorAccessor = "color",
  defaultColor = {day: "cmDarkBlue", night: "cmBlue"},
  hstackparams = {},
  value,
  readOnly,
  qfbuttonparams = {}
}){

  const [selected, setSelected] = useState(defaultValue || (multiple ? [] : null));

  useEffect(() => {
    if(value !== undefined){
      setSelected(value);
    }
  }, [value]);

  const _onChange = useCallback((v) => {
    if(readOnly) return;
    let newSelected;
    if(multiple){
      if(!_.isArray(selected)){
        newSelected = [v];
      } else {
        if(selected.includes(v)){
          newSelected = selected.filter(s => s !== v);
        } else {
          newSelected = [...selected, v];
        }
      }
    } else {
      newSelected = v;
    }
    setSelected(newSelected);
    onChange?.(newSelected);
  }, [onChange, multiple, readOnly, selected]);

  const renderBullet = useCallback((o, i) => {
    let isVal = multiple ? selected.includes(o[valAccessor]) : selected === o[valAccessor];
    return QFButton.Bullet(() => _onChange(o[valAccessor]), o[capAccessor]?.EN, null, { //temporary EN
      bgColor: ColorX.DayNight(o[colorAccessor] ?? defaultColor),
      color: ColorX.DayNight({day: "pureWhite", night: "dmBgDark"}),
      inverted: !isVal,
      key: o[valAccessor] ?? i,
      round: 5,
      mR: 5,
      ...qfbuttonparams
    }); 
  }, [selected, multiple, valAccessor, capAccessor, 
    colorAccessor, defaultColor, _onChange, qfbuttonparams]);

  return (
    <HStack HAlign="[" fitWidthToContent {...hstackparams}>
      {_.map(options, (o, i) => {
        return renderBullet(o, i);
      })}
    </HStack>
  );

}

export default TypeSelector;